import { useEffect, useMemo, useState } from 'react';
import { b2bApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

const STATUS_LABELS = { PENDING: 'In afwachting', APPROVED: 'Goedgekeurd', PAID_OUT: 'Uitbetaald', REJECTED: 'Afgewezen' };
const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800', APPROVED: 'bg-blue-100 text-blue-800',
  PAID_OUT: 'bg-green-100 text-green-800', REJECTED: 'bg-red-100 text-red-700',
};

export default function B2BCommissions() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    b2bApi.commissions().then(r => setCommissions(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load
  }, []);

  const { total, pending } = useMemo(() => {
    let paid = 0, pend = 0;
    for (const c of commissions) {
      if (c.status === 'PAID_OUT') paid += c.amount;
      else if (c.status === 'PENDING') pend += c.amount;
    }
    return { total: paid, pending: pend };
  }, [commissions]);

  return (
    <div data-testid="b2b-commissions-page" className="p-8">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-light text-foreground">Commissies</h1>
      </div>

      <div className="grid grid-cols-2 gap-5 mb-8">
        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">Uitbetaald</p>
          <p data-testid="total-paid" className="text-2xl font-semibold text-green-600">{formatCurrency(total, 'nl')}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">In afwachting</p>
          <p data-testid="total-pending" className="text-2xl font-semibold text-amber-600">{formatCurrency(pending, 'nl')}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={`commissions-skeleton-${i}`} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5 text-left font-semibold">Order</th>
                <th className="px-5 py-3.5 text-left font-semibold">Datum</th>
                <th className="px-5 py-3.5 text-right font-semibold">Commissie</th>
                <th className="px-5 py-3.5 text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {commissions.map(c => (
                <tr key={c._id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-5 py-3.5 font-mono text-foreground">{c.order ? `#${c.order.orderNumber}` : '--'}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{new Date(c.created_at).toLocaleDateString('nl-BE', { dateStyle: 'medium' })}</td>
                  <td className="px-5 py-3.5 text-right font-semibold">{formatCurrency(c.amount, 'nl')}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-muted text-muted-foreground'}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!commissions.length && <p className="text-center py-12 text-muted-foreground text-sm">Geen commissie-entries</p>}
        </div>
      )}
    </div>
  );
}
